import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  current: boolean;
  icon: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation: NavItem[] = [
    {
      name: 'Dashboard', href: '/', current: location.pathname === '/',
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
    },
    {
      name: 'Daily Care', href: '/daily-care', current: location.pathname.startsWith('/daily-care'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
    },
    {
      name: 'Care Plans', href: '/care-plans', current: location.pathname.startsWith('/care-plans'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
    },
    {
      name: 'Support Plans', href: '/support-plans', current: location.pathname.startsWith('/support-plans'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
    },
    {
      name: 'Assessments', href: '/assessments', current: location.pathname.startsWith('/assessments'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
    },
    {
      name: 'Accidents', href: '/incidents', current: location.pathname.startsWith('/incidents'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
    },
    {
      name: 'Household Tasks', href: '/household-tasks', current: location.pathname.startsWith('/household-tasks'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
    },
    {
      name: 'Diary', href: '/diary', current: location.pathname.startsWith('/diary'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
    },
  ];

  if (isAdmin) {
    navigation.splice(1, 0,
      {
        name: 'Residents', href: '/residents', current: location.pathname.startsWith('/residents'),
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
      },
      {
        name: 'Staff', href: '/staff', current: location.pathname.startsWith('/staff'),
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      },
      {
        name: 'Training', href: '/training', current: location.pathname.startsWith('/training'),
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
      }
    );
    navigation.push({
      name: 'Daily Care Logs', href: '/daily-care-logs', current: location.pathname.startsWith('/daily-care-logs'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
    });
    navigation.push({
      name: 'Medical Records', href: '/medical-records', current: location.pathname.startsWith('/medical-records'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
    });
    navigation.push({
      name: 'Finance', href: '/finance', current: location.pathname.startsWith('/finance'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    });
  } else {
    navigation.splice(1, 0, {
      name: 'Training', href: '/training', current: location.pathname.startsWith('/training'),
      icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" /></svg>
    });
  }

  // Find current page name for mobile header
  const currentPage = navigation.find(item => item.current)?.name || 'Dashboard';

  const sidebarContent = (
    <>
      {/* Logo/Header — gradient matching mobile HomeScreen header */}
      <div className="flex items-center gap-3.5 px-5 py-4 gradient-header flex-shrink-0">
        {user?.photo ? (
          <img
            src={user.photo}
            alt={user.name}
            className="w-[52px] h-[52px] rounded-full object-cover"
            style={{ borderWidth: '2px', borderColor: 'rgba(255,255,255,0.5)', borderStyle: 'solid' }}
          />
        ) : (
          <div className="w-[52px] h-[52px] rounded-full bg-white/20 flex items-center justify-center"
               style={{ borderWidth: '2px', borderColor: 'rgba(255,255,255,0.5)', borderStyle: 'solid' }}>
            <span className="text-xl font-bold text-white">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">icare.ai</h1>
          <p className="text-xs text-white/75 font-medium">{user?.name}</p>
        </div>
      </div>

      {/* Navigation — matches mobile SettingsScreen section rows: paddingHorizontal 14, paddingVertical 13, borderRadius 14 */}
      <nav className="flex-1 overflow-y-auto py-3">
        <div className="px-3 space-y-0.5">
          {navigation.map((item) => (
            <button
              key={item.name}
              onClick={() => navigate(item.href)}
              className={`${
                item.current
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
              } w-full text-left px-3.5 py-3 text-base transition-all duration-150 flex items-center gap-3`}
              style={{ borderRadius: '14px' }}
            >
              <span className={`flex-shrink-0 ${item.current ? 'text-primary-600' : 'text-gray-400'}`}>
                {item.icon}
              </span>
              {item.name}
            </button>
          ))}
        </div>
      </nav>

      {/* User section — matches mobile SettingsScreen profileCard: padding 16, borderRadius 16, borderWidth 1, borderColor Colors.border */}
      <div className="border-t border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            {user?.photo ? (
              <img
                src={user.photo}
                alt={user.name}
                className="w-[48px] h-[48px] rounded-full object-cover"
                style={{ borderWidth: '2px', borderColor: '#DBEAFE', borderStyle: 'solid' }}
              />
            ) : (
              <div className="w-[48px] h-[48px] rounded-full bg-primary-50 flex items-center justify-center"
                   style={{ borderWidth: '2px', borderColor: '#DBEAFE', borderStyle: 'solid' }}>
                <span className="text-base font-semibold text-primary-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="ml-3.5 flex-1 min-w-0">
            <p className="text-md font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-sm text-gray-500 capitalize font-normal mt-0.5">{user?.role}</p>
          </div>
        </div>
        {/* Logout — matches mobile: height 52, borderRadius 14, danger colors */}
        <button
          onClick={handleLogout}
          className="w-full h-[52px] bg-danger-dark text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-danger-btn"
          style={{ borderRadius: '14px' }}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Log Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F9FAFB]">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: always visible, Mobile: slide overlay */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-strong border-r border-gray-200 flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:z-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Close button — matches mobile backBtn: w38 h38, borderRadius 10 */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-3 lg:hidden w-[38px] h-[38px] flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-colors"
          style={{ borderRadius: '10px' }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {sidebarContent}
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Top Bar — matches mobile header: paddingHorizontal 16, paddingVertical 12, borderBottomWidth 1, borderBottomColor Colors.border */}
        <div className="sticky top-0 z-30 lg:hidden bg-white px-4 py-3 flex items-center gap-3"
             style={{ borderBottomWidth: '1px', borderBottomColor: '#E5E7EB' }}>
          {/* Hamburger — matches mobile backBtn: w38 h38, borderRadius 10, bg Colors.background */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-[38px] h-[38px] flex items-center justify-center bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
            style={{ borderRadius: '10px' }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-md font-semibold text-gray-900 truncate flex-1">{currentPage}</h1>
          <div className="flex-shrink-0">
            {user?.photo ? (
              <img src={user.photo} alt={user.name} className="w-[38px] h-[38px] rounded-full object-cover" style={{ borderWidth: '2px', borderColor: '#DBEAFE', borderStyle: 'solid' }} />
            ) : (
              <div className="w-[38px] h-[38px] rounded-full bg-primary-50 flex items-center justify-center" style={{ borderWidth: '2px', borderColor: '#DBEAFE', borderStyle: 'solid' }}>
                <span className="text-sm font-semibold text-primary-600">{user?.name?.charAt(0).toUpperCase()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
